import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { customersService } from '../services/customersService';
import { toast } from 'react-toastify';
import { countries } from '../constants/countries';
import { UserRole, PaymentMode } from '../types';
import { RootState } from '../store/store';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';

interface CustomerFormData {
  name: string;
  phone?: string;
  email?: string;
  tcPassport?: string;
  country?: string;
  notes?: string;
  isActive?: boolean;
  paymentMode?: PaymentMode;
}

const CustomerFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const canManageCustomers = user?.role === UserRole.MANAGER ||
                             user?.role === UserRole.ORG_ADMIN ||
                             user?.role === UserRole.PLATFORM_ADMIN;

  const [loading, setLoading] = useState(isEditMode);
  const [customerName, setCustomerName] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dependencies, setDependencies] = useState<{ canDelete: boolean; reasons: string[] } | undefined>();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CustomerFormData>({
    defaultValues: {
      isActive: true,
      paymentMode: PaymentMode.PER_TRANSFER,
    },
  });

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const customer = await customersService.getById(id);
        setCustomerName(customer.name);
        reset({
          name: customer.name,
          phone: customer.phone || '',
          email: customer.email || '',
          tcPassport: customer.tcPassport || '',
          country: customer.country || '',
          notes: customer.notes || '',
          isActive: customer.isActive,
          paymentMode: customer.paymentMode || PaymentMode.PER_TRANSFER,
        });
      } catch (error: any) {
        console.error('Failed to load customer:', error);
        toast.error(t('forms.failedToLoadCustomer'));
        navigate('/app/customers');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate, reset]);

  const onSubmit = async (data: CustomerFormData) => {
    try {
      console.log('Submitting customer data:', data);

      // Clean up empty strings
      const cleanedData = {
        ...data,
        phone: data.phone || undefined,
        email: data.email || undefined,
        tcPassport: data.tcPassport || undefined,
        country: data.country || undefined,
        notes: data.notes || undefined,
        paymentMode: data.paymentMode || PaymentMode.PER_TRANSFER,
      };

      if (isEditMode && id) {
        await customersService.update(id, cleanedData);
        toast.success(t('forms.customerUpdated'));
      } else {
        await customersService.create(cleanedData);
        toast.success(t('forms.customerCreated'));
      }

      navigate('/app/customers');
    } catch (error: any) {
      console.error('Failed to save customer:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        t('forms.failedToSaveCustomer');
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = async () => {
    if (!id) return;

    try {
      const deps = await customersService.checkDependencies(id);
      setDependencies(deps);
    } catch (error) {
      setDependencies(undefined);
    }
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;

    await customersService.delete(id);
    toast.success(t('customers.deleted'));
    setIsDeleteModalOpen(false);
    navigate('/app/customers');
  };

  if (!canManageCustomers) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('forms.accessDenied')}</h2>
          <p className="text-gray-600">{t('forms.noPermissionCustomers')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {isEditMode ? t('forms.editCustomer') : t('forms.newCustomer')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          {isEditMode
            ? t('forms.updateCustomerInfo')
            : t('forms.addNewCustomer')}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        <div className="bg-white shadow sm:rounded-lg p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Customer Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Name *
              </label>
              <input
                type="text"
                {...register('name', { required: 'Name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Booking, Airbnb"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                {...register('phone')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+90 555 123 4567"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="info@customer.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* TC/Passport */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                TC / Passport No
              </label>
              <input
                type="text"
                {...register('tcPassport')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <select
                {...register('country')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ödeme Modu
              </label>
              <select
                {...register('paymentMode')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={PaymentMode.PER_TRANSFER}>Transfer Bazlı</option>
                <option value={PaymentMode.LUMP_SUM}>Toplu Ödeme (Cari)</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Transfer Bazlı: Her transfer için ayrı ödeme takibi. Toplu Ödeme: Cari hesap sistemi.
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                {...register('notes')}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('forms.notesPlaceholder')}
              />
            </div>

            {/* Active Status (only show in edit mode) */}
            {isEditMode && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Users Management Section (only in edit mode) */}
        {isEditMode && id && (
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('users.title')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Bu müşteri için kullanıcı hesapları yönetin. İlk eklenen kullanıcı otomatik olarak Müşteri Yöneticisi olur.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/app/customers/${id}/users`)}
                className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors min-h-[44px] font-medium flex items-center gap-2"
              >
                <span>Kullanıcıları Yönet</span>
                <span>→</span>
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
          {isEditMode ? (
            <button
              type="button"
              onClick={handleDeleteClick}
              className="px-4 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors min-h-[44px] font-medium"
            >
              {t('common.delete')}
            </button>
          ) : (
            <div />
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => navigate('/app/customers')}
              className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 transition-colors min-h-[44px] font-medium"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:bg-blue-400 min-h-[44px] font-medium"
            >
              {isSubmitting
                ? t('common.saving')
                : isEditMode
                ? t('common.update')
                : t('common.create')}
            </button>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDependencies(undefined);
        }}
        onConfirm={handleDeleteConfirm}
        entityName={customerName}
        entityType="customer"
        requirePassword={true}
        dependencies={dependencies}
      />
    </div>
  );
};

export default CustomerFormPage;
